import { z } from "zod/v4";

import { ExpressionNodeOperator } from "./audiences";
import { CartTargetingSchema } from "./cart-targeting";
import { OfferProductInclusionTypeList } from "./offer-products";

export const UpsellProductRulesActionAttributeList = [
  "shopifyRecommendations",
  "productId",
  "collection",
  "tag",
] as const;

export type UpsellProductActionRulesAttribute =
  (typeof UpsellProductRulesActionAttributeList)[number];

export const UpsellProductRulesActionFilterList = [
  "includes",
  "doesNotInclude",
] as const;

export type UpsellProductRulesActionFilter =
  (typeof UpsellProductRulesActionFilterList)[number];

export const UpsellProductVariantSchema = z.object({
  variantId: z.string(),
  variantTitle: z.string(),
  variantImage: z.string().nullish(),
  price: z.number(),
  compareAtPrice: z.number().nullish(),
  currencyCode: z.string().optional(), // Optional for backward compatibility with legacy productId rules
  availableForSale: z.boolean().optional(),
  sig: z.string().nullish(),
  inclusionType: z.enum(OfferProductInclusionTypeList),
  variantOptions: z
    .array(
      z.object({
        name: z.string(),
        value: z.string(),
      })
    )
    .optional(),
});

export type UpsellProductVariant = z.infer<typeof UpsellProductVariantSchema>;

export const UpsellDiscountTypeList = ["none", "percentage", "fixed"] as const;
export type UpsellDiscountType = (typeof UpsellDiscountTypeList)[number];
export type ActiveUpsellDiscountType = Exclude<UpsellDiscountType, "none">;

export const UpsellCompareAtPriceDisplayList = [
  "none",
  "compareAtPrice",
  "regularPrice",
] as const;
export type UpsellCompareAtPriceDisplay =
  (typeof UpsellCompareAtPriceDisplayList)[number];

export const UpsellDiscountSchema = z.object({
  discountType: z.enum(UpsellDiscountTypeList).default("none"),
  discountValue: z.number().nullish(),
  compareAtPriceDisplay: z
    .enum(UpsellCompareAtPriceDisplayList)
    .default("none"),
});
export type UpsellDiscount = z.infer<typeof UpsellDiscountSchema>;

export function isActiveUpsellDiscount(discount: {
  discountType: UpsellDiscountType;
  discountValue?: number | null;
}): discount is {
  discountType: ActiveUpsellDiscountType;
  discountValue: number;
} {
  return (
    discount.discountType !== "none" &&
    discount.discountValue != null &&
    discount.discountValue > 0
  );
}

export const UpsellProductItemSchema = z.object({
  order: z.number(),
  productId: z.string(),
  productTitle: z.string(),
  handle: z.string().nullish(),
  productImage: z.string().nullish(),
  description: z.string().nullish(),
  options: z
    .array(
      z.object({
        name: z.string(),
        values: z.array(z.string()),
      })
    )
    .optional(),
  variants: z.array(UpsellProductVariantSchema),
  // Which variant the surface pre-selects, in the same BARE numeric form as
  // `UpsellProductVariant.variantId` so a render never has to normalise ids to
  // compare them. Set ONLY by Agentic Logic, from the rule's own
  // `defaultVariantId`, and only when that variant survived hydration as an
  // `include` variant — so a surface may seed its picker from it without
  // re-checking inclusion or stock.
  //
  // ABSENT means "no pre-selection": every surface keeps its own long-standing
  // first-includable fallback. That is what legacy (non-Agentic-Logic) upsell
  // rules produce, and their behaviour is unchanged.
  //
  // Lives HERE, on the one product shape: the checkout wire body, the
  // post-purchase offer shape and the preview DTO all parse through this
  // schema, and zod strips what it does not declare.
  defaultVariantId: z.string().nullish(),
});

export type UpsellProductItem = z.infer<typeof UpsellProductItemSchema>;

export const UpsellProductRulesActionExpressionNodeQuery = z.object({
  key: z.enum(UpsellProductRulesActionAttributeList),
  type: z.enum(UpsellProductRulesActionAttributeList),
  filter: z.enum(UpsellProductRulesActionFilterList).optional(),
  value: z
    .union([z.string(), z.number(), z.array(UpsellProductItemSchema)])
    .optional(),
});

export type UpsellProductRulesActionQuery = z.infer<
  typeof UpsellProductRulesActionExpressionNodeQuery
>;

export const UpsellProductRulesNode = z
  .object({
    query: UpsellProductRulesActionExpressionNodeQuery.optional(),
    operator: ExpressionNodeOperator.optional(),
  })
  .refine(
    (val) => (val.operator && !val.query) || (!val.operator && val.query),
    { message: "must have one of 'operator' or 'query', and not both" }
  )
  .refine((val) => !val.operator || val.operator === "and", {
    message: "only AND operator is supported for upsell product rules",
  });

export type UpsellProductRulesNodeType = z.infer<typeof UpsellProductRulesNode>;

const BaseUpsellProductRulesActionSchema = UpsellDiscountSchema.extend({
  expression: z.array(UpsellProductRulesNode),
  discountType: z.enum(UpsellDiscountTypeList).default("none"),
  discountValue: z.number().nullish(),
  compareAtPriceDisplay: z
    .enum(UpsellCompareAtPriceDisplayList)
    .default("none"),
});

export type UpsellProductRulesAction = z.infer<
  typeof BaseUpsellProductRulesActionSchema
> & {
  onAccept?: UpsellProductRulesAction | null;
  onDecline?: UpsellProductRulesAction | null;
};

export const UpsellProductRulesActionSchema: z.ZodType<UpsellProductRulesAction> =
  BaseUpsellProductRulesActionSchema.extend({
    onAccept: z.lazy(() => UpsellProductRulesActionSchema).nullish(),
    onDecline: z.lazy(() => UpsellProductRulesActionSchema).nullish(),
  });

export const UpsellProductRulesSchema = z.object({
  id: z.uuid(),
  checkoutBlockId: z.uuid(),
  priority: z.number().int().min(0),
  cartTargeting: CartTargetingSchema,
  action: UpsellProductRulesActionSchema,
  variationId: z.uuid(),
});

export type UpsellProductRulesDto = z.infer<typeof UpsellProductRulesSchema>;

export const CreateUpsellProductRulesDtoSchema = UpsellProductRulesSchema.omit({
  id: true,
});

export type CreateUpsellProductRulesDto = z.infer<
  typeof CreateUpsellProductRulesDtoSchema
>;
